<template>
  <div class="pgs-home-ayb">
    <!-- Header with Dropdown -->
    <v-row class="mb-4">
      <v-col cols="12" md="4">
        <v-select v-model="selectedDepto" :items="departamentos" item-text="nm" item-value="id"
          label="Seleccionar Departamento A&B" outlined dense hide-details color="primary" @change="onDeptoChange"
          prepend-inner-icon="mdi-silverware-variant" class="rounded-lg" background-color="white"></v-select>
      </v-col>
    </v-row>

    <!-- Contenido Principal -->
    <div v-if="selectedDepto">
      <!-- KPI Cards -->
      <v-row class="mb-6">
        <!-- Mesas Abiertas Card -->
        <v-col cols="12" md="6">
          <v-card flat class="pgs-kpi-card pa-5 rounded-xl h-100 d-flex flex-column justify-space-between">
            <div>
              <div class="d-flex align-center justify-space-between mb-3">
                <div class="d-flex align-center">
                  <div class="pgs-kpi-icon-pill pgs-kpi-icon-blue mr-3">
                    <v-icon size="18" color="inherit">mdi-table-furniture</v-icon>
                  </div>
                  <div>
                    <div class="pgs-kpi-label">Mesas Abiertas</div>
                    <div class="text-caption pgs-text-muted line-height-tight">Operación actual</div>
                  </div>
                </div>
                <div class="pgs-badge-live">
                  <span class="pgs-badge-dot"></span>
                  <span>En vivo</span>
                </div>
              </div>

              <div class="d-flex align-baseline justify-space-between my-2">
                <div class="d-flex align-baseline">
                  <span v-if="loading.mesas" class="text-h4 font-weight-bold pgs-text-primary mr-2">
                    <v-progress-circular indeterminate color="primary" size="28" width="3"></v-progress-circular>
                  </span>
                  <template v-else>
                    <span class="pgs-kpi-metric mr-2">{{ stats.openTables }}</span>
                    <span class="pgs-kpi-unit">Mesas</span>
                  </template>
                </div>
              </div>
            </div>
          </v-card>
        </v-col>

        <!-- Ocupación Card -->
        <v-col cols="12" md="6">
          <v-card flat class="pgs-kpi-card pa-5 rounded-xl h-100 d-flex flex-column justify-space-between">
            <div>
              <div class="d-flex align-center justify-space-between mb-3">
                <div class="d-flex align-center">
                  <div class="pgs-kpi-icon-pill pgs-kpi-icon-emerald mr-3">
                    <v-icon size="18" color="inherit">mdi-account-group</v-icon>
                  </div>
                  <div>
                    <div class="pgs-kpi-label">Ocupación</div>
                    <div class="text-caption pgs-text-muted line-height-tight">Comensales actuales</div>
                  </div>
                </div>
                <div class="pgs-badge-live">
                  <span class="pgs-badge-dot"></span>
                  <span>En vivo</span>
                </div>
              </div>

              <div class="d-flex align-baseline justify-space-between my-2">
                <div class="d-flex align-baseline">
                  <span v-if="loading.mesas" class="text-h4 font-weight-bold pgs-text-primary mr-2">
                    <v-progress-circular indeterminate color="success" size="28" width="3"></v-progress-circular>
                  </span>
                  <template v-else>
                    <span class="pgs-kpi-metric mr-2">{{ stats.totalPax }}</span>
                    <span class="pgs-kpi-unit">Pax</span>
                  </template>
                </div>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Charts Row -->
      <v-row class="mb-6">
        <v-col cols="12" md="6">
          <v-card flat class="pgs-dashboard-panel rounded-xl h-100 pa-4">
            <div v-if="loading.mesas" class="d-flex justify-center align-center h-100 min-h-300">
              <v-progress-circular indeterminate color="primary" size="48" width="4"></v-progress-circular>
            </div>
            <fusioncharts v-else type="bar2d" width="100%" height="350" dataFormat="json"
              :dataSource="chartDataMeseros">
            </fusioncharts>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card flat class="pgs-dashboard-panel rounded-xl h-100 pa-4">
            <div v-if="loading.mesas" class="d-flex justify-center align-center h-100 min-h-300">
              <v-progress-circular indeterminate color="primary" size="48" width="4"></v-progress-circular>
            </div>
            <fusioncharts v-else type="spline" width="100%" height="350" dataFormat="json"
              :dataSource="chartDataHora">
            </fusioncharts>
          </v-card>
        </v-col>
      </v-row>

      <!-- Tabla Mesas Abiertas -->
      <v-row>
        <v-col cols="12">
          <v-card flat class="pgs-dashboard-panel rounded-xl h-100">
            <div class="pa-4 border-bottom-light d-flex align-center justify-space-between">
              <h3 class="text-h6 font-weight-bold pgs-text-primary mb-0">Mesas Activas</h3>
            </div>

            <v-data-table :headers="mesasHeaders" :items="openTablesList" :loading="loading.mesas" hide-default-footer
              disable-pagination class="pgs-custom-table">
              <template v-slot:item.nombre="{ item }">
                <div class="font-weight-medium text-slate-800">{{ item.Nombre }}</div>
              </template>
              
              <template v-slot:item.cliente="{ item }">
                <div class="d-flex align-center py-2">
                  <v-avatar size="32" color="#EFF6FF" class="mr-3">
                    <span class="font-weight-bold" style="color: #3B82F6; font-size: 12px;">
                      {{ getClienteName(item).charAt(0).toUpperCase() }}
                    </span>
                  </v-avatar>
                  <div class="font-weight-medium text-body-2 text-slate-800 text-truncate" style="max-width: 150px;" :title="getClienteName(item)">
                    {{ getClienteName(item) }}
                  </div>
                </div>
              </template>
              
              <template v-slot:item.mesero="{ item }">
                <div class="text-caption pgs-text-secondary text-truncate" style="max-width: 150px;" :title="getMeseroName(item)">
                  {{ getMeseroName(item) }}
                </div>
              </template>
              
              <template v-slot:item.cuentas="{ item }">
                <v-chip x-small color="#F1F5F9" text-color="#475569" class="font-weight-medium px-2 rounded-md">
                  {{ getCuentasCount(item) }}
                </v-chip>
              </template>
              
              <template v-slot:item.apertura="{ item }">
                <div class="d-flex align-center">
                  <v-icon size="14" color="primary" class="mr-1">mdi-clock-outline</v-icon>
                  <span class="text-caption font-weight-medium" style="color: #0F172A;">{{ formatTime(item.upat || item.crat) }}</span>
                </div>
              </template>

              <template v-slot:item.comensales="{ item }">
                <v-chip small color="#F8FAFC" text-color="#475569" class="font-weight-medium border-light px-3 rounded-lg">
                  <v-icon left size="14" color="primary">mdi-account-group</v-icon>
                  {{ item.comensales }} Pax
                </v-chip>
              </template>
              <template v-slot:no-data>
                <div class="pa-6 text-center">
                  <v-icon size="48" color="grey lighten-2" class="mb-3">mdi-table-off</v-icon>
                  <div class="text-body-1 font-weight-medium pgs-text-muted">No hay mesas abiertas</div>
                </div>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Empty State -->
    <v-row v-else class="h-100 align-center justify-center pt-10">
      <v-col cols="12" md="6" class="text-center">
        <v-card flat class="pa-8 rounded-xl bg-slate-50 border-light d-flex flex-column align-center">
          <v-avatar color="white" size="80" class="elevation-1 mb-4">
            <v-icon size="40" color="primary">mdi-silverware-variant</v-icon>
          </v-avatar>
          <h2 class="text-h5 font-weight-bold pgs-text-primary mb-2">Selecciona un Departamento</h2>
          <p class="text-body-1 pgs-text-secondary text-center mb-0">Elige un departamento de A&B para ver la
            información en
            tiempo real de la operación.</p>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import moment from "moment";
import { mapGetters } from "vuex";

export default {
  name: "HomeAYB",
  data() {
    return {
      selectedDepto: null,
      actualListener: null,
      unsubscribeContenedores: null,
      departamentos: [],
      openTablesList: [],
      loading: {
        mesas: false
      },
      stats: {
        openTables: 0,
        totalPax: 0
      },
      mesasHeaders: [
        { text: 'Mesa / Contenedor', value: 'nombre', sortable: true },
        { text: 'Cliente', value: 'cliente', sortable: true },
        { text: 'Mesero', value: 'mesero', sortable: true },
        { text: 'Cuentas', value: 'cuentas', sortable: false, align: 'center' },
        { text: 'Actualización', value: 'apertura', sortable: true },
        { text: 'Comensales', value: 'comensales', sortable: true, align: 'end' },
      ],
      chartDataMeseros: {
        chart: {
          caption: "Carga de Trabajo por Mesero",
          subCaption: "Pax actuales atendidos",
          xAxisName: "Mesero",
          yAxisName: "Comensales",
          theme: "fusion",
          bgColor: "#ffffff",
          showBorder: "0",
          palettecolors: "#3B82F6",
        },
        data: []
      },
      chartDataHora: {
        chart: {
          caption: "Afluencia por Hora (Mesas Abiertas)",
          subCaption: "Conteo de pax según hora de ingreso hoy",
          xAxisName: "Hora",
          yAxisName: "Comensales",
          theme: "fusion",
          bgColor: "#ffffff",
          showBorder: "0",
          palettecolors: "#10B981",
          drawAnchors: "1",
          anchorRadius: "4",
          anchorBgColor: "#10B981"
        },
        data: []
      }
    };
  },
  computed: {
    ...mapGetters(["getFirebaseApp"])
  },
  methods: {
    getClienteName(item) {
      if (item.Estado && item.Estado.Cliente) {
        return item.Estado.Cliente.Nom || item.Estado.Cliente.Apd || "Público General";
      }
      return "Público General";
    },
    getMeseroName(item) {
      if (item.Estado && item.Estado.Mesero) {
        return item.Estado.Mesero.Nom || "Sin asignar";
      }
      return "Sin asignar";
    },
    getCuentasCount(item) {
      if (item.Estado && item.Estado.Cuentas) {
        return Object.keys(item.Estado.Cuentas).length;
      }
      return 0;
    },
    formatTime(timestamp) {
      if (!timestamp) return '--:--';
      return moment(timestamp).format('HH:mm a');
    },
    async getDepartamentos() {
      try {
        const snapshot = await this.getFirebaseApp.database.queryBuilder("AYB/Departamentos");
        if (snapshot.exists()) {
          const deptosObj = snapshot.val();
          this.departamentos = Object.keys(deptosObj).map(key => {
            return { id: key, nm: deptosObj[key].Nom || deptosObj[key].Nombre };
          }).filter(d => !d.del);

          if (this.departamentos.length > 0) {
            this.selectedDepto = this.departamentos[0].id;
            this.onDeptoChange();
          }
        }
      } catch (e) {
        console.error("Error fetching AYB/Departamentos", e);
      }
    },
    onDeptoChange() {
      if (this.selectedDepto) {
        this.fetchMesasRTDB();
      }
    },
    killListener(deptoId) {
      if (this.unsubscribeContenedores) {
        this.unsubscribeContenedores();
        this.unsubscribeContenedores = null;
      }
      if (deptoId) {
        try {
          const ref = this.getFirebaseApp.database.ref("AYB/Contenedores/" + deptoId);
          this.getFirebaseApp.database.off(ref);
        } catch (e) {
          console.warn("Error calling database.off:", e);
        }
      }
    },
    fetchMesasRTDB() {
      const deptoId = this.selectedDepto;

      if (this.actualListener) {
        this.killListener(this.actualListener);
      }

      this.loading.mesas = true;

      this.unsubscribeContenedores = this.getFirebaseApp.database.queryBuilderListener(
        "AYB/Contenedores/" + deptoId,
        null,
        (snapshot) => {
          try {
            if (snapshot.exists()) {
              let contenedoresObj = snapshot.val();
              let openTables = 0;
              let totalPax = 0;
              let list = [];
              let agrupacionMeseros = {};
              let agrupacionHoras = {};

              Object.keys(contenedoresObj).map(key => {
                let mesa = contenedoresObj[key];
                mesa.id = key;

                // Determinamos si está abierta 
                let isAbierta = false;
                let pax = 0;

                if (mesa.Estado && mesa.Estado.E == 1) {
                  isAbierta = true;
                } else if (mesa.Estado && mesa.Estado.Cuentas && Object.keys(mesa.Estado.Cuentas).length > 0) {
                  isAbierta = true;
                }

                if (isAbierta) {
                  openTables++;

                  if (mesa.Estado && mesa.Estado.Comensales) {
                    pax = Number(mesa.Estado.Comensales);
                  } else if (mesa.Comensales) {
                    pax = Number(mesa.Comensales);
                  } else {
                    pax = 1;
                  }

                  totalPax += pax;

                  list.push({
                    id: mesa.id,
                    nombre: mesa.Nombre || mesa.Nom || "Mesa",
                    comensales: pax,
                    ...mesa
                  });
                  
                  // Agrupación para Meseros
                  let meseroNom = (mesa.Estado && mesa.Estado.Mesero && mesa.Estado.Mesero.Nom) ? mesa.Estado.Mesero.Nom : "Sin asignar";
                  if (!agrupacionMeseros[meseroNom]) {
                    agrupacionMeseros[meseroNom] = 0;
                  }
                  agrupacionMeseros[meseroNom] += pax;

                  // Agrupación para Hora
                  let timestamp = mesa.upat || mesa.crat;
                  let hora = timestamp ? moment(timestamp).format("HH:00") : "Sin hora";
                  if (!agrupacionHoras[hora]) {
                    agrupacionHoras[hora] = 0;
                  }
                  agrupacionHoras[hora] += pax;
                }
              });

              this.stats.openTables = openTables;
              this.stats.totalPax = totalPax;
              this.openTablesList = list;
              
              // Cargar datos a gráficas
              this.chartDataMeseros.data = Object.keys(agrupacionMeseros).map(key => ({
                label: key,
                value: agrupacionMeseros[key]
              })).sort((a, b) => b.value - a.value);

              let horaKeys = Object.keys(agrupacionHoras).sort();
              this.chartDataHora.data = horaKeys.map(key => ({
                label: key,
                value: agrupacionHoras[key]
              }));

            } else {
              // Limpiar data
              this.stats.openTables = 0;
              this.stats.totalPax = 0;
              this.openTablesList = [];
              this.chartDataMeseros.data = [];
              this.chartDataHora.data = [];
            }
          } catch (e) {
            console.error("Error parsing Contenedores stats", e);
          } finally {
            this.loading.mesas = false;
          }
        }
      );
      this.actualListener = deptoId;
    }
  },
  beforeDestroy() {
    if (this.actualListener) {
      this.killListener(this.actualListener);
    }
  },
  mounted() {
    this.getDepartamentos();
  }
};
</script>

<style scoped>
.pgs-home-ayb {
  font-family: 'Inter', -apple-system, sans-serif;
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px;
}

.pgs-text-primary {
  color: #0F172A !important;
}

.pgs-text-secondary {
  color: #475569 !important;
}

.pgs-text-muted {
  color: #64748B !important;
}

.text-emerald {
  color: #10B981 !important;
}

.bg-emerald {
  background-color: #10B981 !important;
}

.bg-amber-soft {
  background-color: #FBBF24 !important;
}

.line-height-tight {
  line-height: 1.2;
}

.min-h-300 {
  min-height: 300px;
}

/* Dashboard Panels & Cards */
.pgs-dashboard-panel,
.pgs-kpi-card {
  border: 1px solid #E2E8F0 !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03) !important;
  background-color: #FFFFFF !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.pgs-kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04) !important;
}

.bg-slate-50 {
  background-color: #F8FAFC !important;
}

.border-light {
  border: 1px dashed #CBD5E1 !important;
}

.border-bottom-light {
  border-bottom: 1px solid #F1F5F9 !important;
}

/* KPI Elements */
.pgs-kpi-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #334155;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.pgs-kpi-metric {
  font-size: 2.25rem;
  font-weight: 800;
  color: #0F172A;
  line-height: 1;
  letter-spacing: -0.025em;
}

.pgs-kpi-unit {
  font-size: 0.875rem;
  font-weight: 500;
  color: #64748B;
  margin-bottom: 4px;
}

/* Icon Pills */
.pgs-kpi-icon-pill {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pgs-kpi-icon-blue {
  background-color: #EFF6FF;
  color: #3B82F6;
}

.pgs-kpi-icon-emerald {
  background-color: #ECFDF5;
  color: #10B981;
}

/* Badges & Indicators */
.pgs-badge-live {
  display: inline-flex;
  align-items: center;
  background-color: #FEF2F2;
  color: #EF4444;
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.025em;
}

.pgs-badge-dot {
  width: 6px;
  height: 6px;
  background-color: #EF4444;
  border-radius: 50%;
  margin-right: 6px;
  animation: pulse-red 2s infinite;
}

@keyframes pulse-red {
  0% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4);
  }

  70% {
    box-shadow: 0 0 0 4px rgba(239, 68, 68, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
  }
}

/* Custom Table Styles */
::v-deep .pgs-custom-table {
  background-color: transparent !important;
}

::v-deep .pgs-custom-table th {
  background-color: #F8FAFC !important;
  color: #475569 !important;
  font-weight: 600 !important;
  font-size: 0.75rem !important;
  text-transform: uppercase !important;
  letter-spacing: 0.05em !important;
  border-bottom: 1px solid #E2E8F0 !important;
  height: 48px !important;
}

::v-deep .pgs-custom-table td {
  border-bottom: 1px solid #F1F5F9 !important;
  height: 60px !important;
  font-size: 0.875rem !important;
}

::v-deep .pgs-custom-table tbody tr:hover {
  background-color: #F8FAFC !important;
}
</style>
